import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Play, Pause, Download, Search, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function SendBatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    if (searchTerm) {
      setFilteredItems(items.filter(item => 
        item.recipient_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.file_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      ));
    } else {
      setFilteredItems(items);
    }
  }, [searchTerm, items]);

  const loadData = async () => {
    try {
      const [batchRes, itemsRes] = await Promise.all([
        api.get(`/send-batches/${id}`),
        api.get(`/send-batches/${id}/items`)
      ]);
      setBatch(batchRes.data);
      setItems(itemsRes.data);
      setFilteredItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const queueBatch = async () => {
    try {
      await api.post(`/send-batches/${id}/queue`);
      toast.success('Batch queued for review');
      loadData();
    } catch (error) {
      toast.error('Failed to queue batch');
    }
  };

  const sendBatch = async () => {
    try {
      await api.post(`/send-batches/${id}/send`);
      toast.success('Batch sending started');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send batch');
    }
  };

  const pauseBatch = async () => {
    try {
      await api.post(`/send-batches/${id}/pause`);
      toast.success('Batch paused');
      loadData();
    } catch (error) {
      toast.error('Failed to pause batch');
    }
  };

  const exportResults = async () => {
    window.open(`${api.defaults.baseURL}/send-batches/${id}/export-results`, '_blank');
  };

  const exportOpens = async () => {
    window.open(`${api.defaults.baseURL}/send-batches/${id}/export-opens`, '_blank');
  };

  const deleteSendBatch = async () => {
    try {
      await api.delete(`/send-batches/${id}`);
      toast.success('Send batch deleted');
      setDeleteDialogOpen(false);
      navigate('/send-batches');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete send batch');
    }
  };

  if (!batch) return <div className="text-white">Loading...</div>;

  const processedCount = batch.processed_count ?? ((batch.sent_count || 0) + (batch.failed_count || 0) + (batch.skipped_count || 0));
  const progress = batch.progress_percent ?? (batch.total_recipients > 0 ? (processedCount / batch.total_recipients) * 100 : 0);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'skipped': return <XCircle className="w-4 h-4 text-gray-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-blue-600" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="send-batch-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/send-batches')} className="text-white hover:bg-white/20" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-4xl font-bold text-white flex-1" style={{fontFamily: 'Space Grotesk'}}>
          {batch.subject || 'Send Batch Details'}
        </h1>
        <div className={`status-badge status-${batch.status}`}>{batch.status}</div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Send Batch</AlertDialogTitle>
            <AlertDialogDescription>
              Delete this send batch and its recipient tracking history? Sending batches must be paused before they can be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSendBatch} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid md:grid-cols-5 gap-6">
        <Card className="glass-card" data-testid="total-recipients-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Recipients</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{batch.total_recipients}</div>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="processed-count-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Processed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{processedCount}</div>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="sent-count-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Sent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{batch.sent_count}</div>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="failed-count-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{batch.failed_count}</div>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="pending-count-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{batch.pending_count}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Progress</CardTitle>
            <div className="flex gap-2">
              {batch.status === 'draft' && (
                <Button onClick={queueBatch} size="sm" data-testid="queue-button">
                  <Play className="w-4 h-4 mr-2" />
                  Queue
                </Button>
              )}
              {batch.status === 'queued' && (
                <Button onClick={sendBatch} size="sm" data-testid="send-button">
                  <Play className="w-4 h-4 mr-2" />
                  Send
                </Button>
              )}
              {(batch.status === 'sending' || batch.status === 'queued') && (
                <Button onClick={pauseBatch} size="sm" variant="outline" data-testid="pause-button">
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </Button>
              )}
              <Button onClick={exportResults} size="sm" variant="outline" data-testid="export-results-button">
                <Download className="w-4 h-4 mr-2" />
                Export Results
              </Button>
              {batch.mode === 'link' && (
                <Button onClick={exportOpens} size="sm" variant="outline" data-testid="export-opens-button">
                  <Download className="w-4 h-4 mr-2" />
                  Export Opens
                </Button>
              )}
              {batch.status !== 'sending' && (
                <Button onClick={() => setDeleteDialogOpen(true)} size="sm" variant="outline" className="text-red-600" data-testid="delete-send-batch-button">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Progress value={progress} className="flex-1 h-3" />
            <span className="font-medium">{Number(progress).toFixed(1)}%</span>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            {processedCount} of {batch.total_recipients} processed. Sending continues in the backend while users are logged out or working elsewhere.
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Recipients ({filteredItems.length})</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search recipients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredItems.map(item => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`recipient-item-${item.id}`}>
                <div className="flex items-center gap-3">
                  {getStatusIcon(item.status)}
                  <div>
                    <div className="font-medium">{item.recipient_email}</div>
                    <div className="text-sm text-gray-500">{item.file_name || 'Manual email'}</div>
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <div className={`text-sm font-medium ${
                    item.status === 'sent' ? 'text-green-600' : 
                    item.status === 'failed' ? 'text-red-600' : 
                    item.status === 'skipped' ? 'text-gray-600' :
                    'text-blue-600'
                  }`}>
                    {item.status.toUpperCase()}
                  </div>
                  {item.status === 'sent' && item.sent_at && (
                    <div className="text-xs text-gray-500">
                      {new Date(item.sent_at).toLocaleString()}
                    </div>
                  )}
                  {item.file_id && batch.mode === 'link' && item.download_count > 0 && (
                    <div className="text-xs text-purple-600 font-medium">
                      Downloads: {item.download_count}
                    </div>
                  )}
                  {item.last_error && (
                    <div className="text-xs text-red-600 max-w-xs truncate" title={item.last_error}>
                      {item.last_error}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
